/**
 * src/api/employeeApi.js
 *
 * Thin client for the employees-write Azure Function — the dedicated
 * per-record write path for employee records (ADR-0008,
 * docs/decisions/0008-employees-per-record-storage.md, EMPA-03). Mirrors
 * assetApi.js's / vendorApi.js's shape: one wrapper per action, each returning
 * the function's response body so callers can reconcile local state without a
 * full reload.
 *
 * Two rules this client exists to enforce (ADR-0008 §2.4):
 *
 *   1. Post ONLY the records that changed, each with the version it was
 *      changed FROM. Resubmitting an untouched-yet-stale copy of an employee
 *      is asking to revert somebody else's concurrent edit to that employee,
 *      and correctly earns a 409.
 *   2. Deletion is explicit. There is no "post the array without them" call
 *      here, because absence from an array is "this client never fetched it"
 *      at least as often as it is a deletion — the ambiguity behind GH #3903.
 */

import { azurePost, azureEnvPost } from './azureClient';
import { employeeVersionsFor, mergeEmployeeVersions } from '../state/employeeVersions.js';

/**
 * Same env-routing contract as src/api/storage-azure.js (STG-B-002): a tenant
 * caller omits `env` and the tenant is resolved from its JWT, so `tenantId` is
 * NOT sent; a super-admin passes `env` and names the tenant explicitly,
 * because a super-admin JWT carries no tenant_id.
 */
async function callEmployeesWrite(action, body, tenantId, opts = {}) {
  const path = `/api/employees-write?action=${encodeURIComponent(action)}`;
  if (opts.env) return azureEnvPost(opts.env, path, { ...body, tenantId });
  return azurePost(path, body);
}

/**
 * Create-or-update the given employee records — and only those.
 * Returns { ok, employees, versions }; the new versions are adopted here so
 * the caller's next write for the same records carries a current token.
 *
 * @param {object[]} employees the CHANGED records, never the whole array
 * @param {string} tenantId
 * @param {{ overrides?: Array<{employeeId: string, path: Array}> }} [opts]
 *   fields the user chose "keep mine" on (CONC-01) — the server logs each one
 *   that this save actually overwrote
 */
export async function saveEmployeeRecords(employees, tenantId, opts = {}) {
  const { overrides, ...callOpts } = opts;
  const ids = (employees || []).map((e) => e && e.id).filter(Boolean);
  const result = await callEmployeesWrite(
    'save',
    {
      employees,
      expectedVersions: employeeVersionsFor(ids),
      ...(Array.isArray(overrides) && overrides.length ? { overrides } : {}),
    },
    tenantId,
    callOpts
  );
  mergeEmployeeVersions(result?.versions);
  return result;
}

/**
 * Record a competence assessment for one role requirement (RCQA-01/03).
 * Only the server stamps who assessed, when and on what basis, and reads the
 * requirement from the role as stored — so this is its own action, never a
 * whole-record save. An assessment is an append: two assessors recording for
 * one employee both land.
 *
 * @param {{ employeeId: string, roleId: string, requirementId: string,
 *   level?: number, met?: boolean, effectivenessAssessed?: boolean,
 *   evidence?: { artefactId?: string, note?: string }, expiresAt?: string }} body
 */
export async function recordCompetenceAssessment(body, tenantId) {
  const {
    employeeId,
    roleId,
    requirementId,
    level,
    met,
    effectivenessAssessed,
    evidence,
    expiresAt,
  } = body || {};
  const result = await callEmployeesWrite(
    'record-competence-assessment',
    {
      employeeId,
      roleId,
      requirementId,
      ...(level != null ? { level } : {}),
      ...(typeof met === 'boolean' ? { met } : {}),
      effectivenessAssessed: effectivenessAssessed === true,
      ...(evidence ? { evidence } : {}),
      ...(expiresAt ? { expiresAt } : {}),
    },
    tenantId
  );
  mergeEmployeeVersions(result?.versions);
  return result;
}

/** Create-or-update ONE employee record. Returns { ok, employees, versions }. */
export async function saveEmployeeRecord(employee, tenantId) {
  return saveEmployeeRecords([employee], tenantId);
}

/** Delete one employee by id — explicit, never inferred. Returns { ok }. */
export async function deleteEmployeeRecord(employeeId, tenantId, opts = {}) {
  return callEmployeesWrite('delete', { employeeId }, tenantId, opts);
}

/**
 * Replace a tenant's WHOLE employee list — super-admin only, and the only call
 * here that removes employees absent from the array. It exists for the
 * super-admin "reset to demo data" action, which deliberately replaces the
 * list; the server refuses `mode: 'replace'` for anyone else. Same carve-out,
 * for the same reason, as writeTenantUsers(..., { replace: true }).
 *
 * Without it the demo reset writes a fresh employee list into the tenant_state
 * blob while dbo.tenant_employees keeps the old one — and the rows are what
 * state-read serves.
 */
export async function replaceEmployeeRecords(employees, tenantId, opts = {}) {
  return callEmployeesWrite('save', { employees, mode: 'replace' }, tenantId, opts);
}

/**
 * Record that an employee has read a document, on their behalf (RDRC-04A).
 * The server stamps who recorded it, when, and `method: 'on-behalf'`; the
 * client sends only the document, a reason (required, 1..500) and an optional
 * evidence reference. A whole-record save that adds an acknowledgement is
 * refused (ERR_ACK_RECORD_VIA_ACTION), so this is the only way to record one.
 * Never for the caller's own record (ERR_ON_BEHALF_SELF).
 *
 * Returns { ok, acknowledgement, employee, versions, alreadyRecorded? }; the
 * employee's new version is adopted here, like saveEmployeeRecords.
 *
 * @param {{ employeeId: string, documentId: string, reason: string, evidenceRef?: string }} body
 */
export async function recordAcknowledgementOnBehalf(body, tenantId) {
  const { employeeId, documentId, reason, evidenceRef } = body || {};
  const result = await callEmployeesWrite(
    'record-acknowledgement',
    { employeeId, documentId, reason, ...(evidenceRef ? { evidenceRef } : {}) },
    tenantId
  );
  mergeEmployeeVersions(result?.versions);
  return result;
}
